import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, ProgressBar } from 'react-bootstrap';
import API from '../services/api';
import { formatDay } from '../utils/dates';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

// What the backend's stateOf (services/commitments.js) means, for people.
const STATE = {
  active: { label: 'Al día', bg: 'success' },
  overdue: { label: 'Vencido', bg: 'danger' },
  paid: { label: 'Saldado', bg: 'secondary' },
  closed: { label: 'Cerrado', bg: 'dark' },
};
const share = (c) => Math.min(100, (c.paid / c.amount) * 100);
// The same bar as Conduces → Acuerdos: green while it is being paid, grey once done.
const barVariant = (c) => (['paid', 'closed'].includes(c.state) ? 'secondary' : c.state === 'overdue' ? 'danger' : 'success');

// Gastos → "Compromisos" (admins; backend routes/commitments.js): obligations being paid off. Each payment is a gasto linked
// to one (from the Gastos form, or by the WhatsApp assistant, which applies every transfer to a compromiso's account).
const CommitmentsPanel = ({ onChanged }) => {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // {} = new, a compromiso = edit
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => API.get('/commitments').then(r => setList(r.data)).catch(() => setError('No se pudieron cargar los compromisos.')), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };

  const open = list ? list.filter(c => ['active', 'overdue'].includes(c.state)) : [];
  const done = list ? list.filter(c => ['paid', 'closed'].includes(c.state)) : [];
  const owed = open.reduce((s, c) => s + Math.max(0, c.balance), 0);

  const rows = (items) => items.map(c => (
    <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => setViewing(c._id)}>
      <td><strong>{c.name}</strong>{c.payee && <div className="small text-muted">{c.payee}</div>}</td>
      <td className="small">{c.dueDate ? formatDay(c.dueDate) : '—'}</td>
      <td className="text-end">{money(c.amount)}</td>
      <td style={{ minWidth: 180 }}>
        <ProgressBar now={share(c)} variant={barVariant(c)} style={{ height: 8 }} />
        <div className="small text-muted mt-1">{money(c.paid)} pagado · {c.payments} pago{c.payments === 1 ? '' : 's'}</div>
      </td>
      <td className="text-end"><strong>{money(c.balance)}</strong></td>
      <td><Badge bg={STATE[c.state]?.bg}>{STATE[c.state]?.label}</Badge></td>
    </tr>
  ));

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div className="text-muted small" style={{ maxWidth: 760 }}>
          Obligaciones que se van pagando. Cada pago es un gasto del compromiso; las transferencias a una de sus cuentas se le
          aplican solas cuando llegan por WhatsApp.
          {open.length > 0 && <div className="mt-1">Pendiente en total: <strong style={{ color: 'var(--mm-cyan)' }}>{money(owed)}</strong></div>}
        </div>
        <Button onClick={() => setEditing({})}>+ Nuevo compromiso</Button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <Card className="shadow-sm p-3 mb-3">
        {list === null ? <div className="text-muted py-3">Cargando...</div> : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead><tr><th>Compromiso</th><th>Fecha límite</th><th className="text-end">Total</th><th>Pagado</th><th className="text-end">Pendiente</th><th>Estado</th></tr></thead>
              <tbody>
                {rows(open)}
                {open.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">No hay compromisos pendientes.</td></tr>}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
      {done.length > 0 && (
        <Card className="shadow-sm p-3">
          <Card.Title className="small text-muted text-uppercase">Saldados y cerrados</Card.Title>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle"><tbody>{rows(done)}</tbody></Table>
          </div>
        </Card>
      )}
      {editing && <CommitmentForm commitment={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); changed(); }} />}
      {viewing && (
        <CommitmentDetail id={viewing} onClose={() => setViewing(null)} onEdit={(c) => { setViewing(null); setEditing(c); }} onChanged={changed} />
      )}
    </>
  );
};

function CommitmentForm({ commitment, onClose, onSaved }) {
  const isNew = !commitment._id;
  const [form, setForm] = useState({
    name: commitment.name || '', amount: commitment.amount ?? '', payee: commitment.payee || '', dueDate: commitment.dueDate || '',
    notes: commitment.notes || '', accounts: (commitment.accounts || []).map(a => ({ number: a.number, label: a.label || '' })),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setAccount = (i, k) => (e) => setForm(f => ({ ...f, accounts: f.accounts.map((a, j) => (j === i ? { ...a, [k]: e.target.value } : a)) }));
  const save = async () => {
    setSaving(true); setError('');
    const body = {
      name: form.name, amount: Number(form.amount), payee: form.payee, dueDate: form.dueDate || null, notes: form.notes,
      accounts: form.accounts.filter(a => a.number.trim()),
    };
    try {
      if (isNew) await API.post('/commitments', body);
      else await API.put(`/commitments/${commitment._id}`, body);
      onSaved();
    } catch (err) { setError(err.response?.data?.message || 'No se pudo guardar el compromiso.'); }
    finally { setSaving(false); }
  };
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>{isNew ? 'Nuevo compromiso' : 'Editar compromiso'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form.Group className="mb-3">
          <Form.Label>Nombre</Form.Label>
          <Form.Control maxLength={80} value={form.name} onChange={set('name')} placeholder="Ej.: Préstamo camión" autoFocus={isNew} />
        </Form.Group>
        <Row>
          <Col><Form.Group className="mb-3">
            <Form.Label>Total adeudado (RD$)</Form.Label>
            <Form.Control type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} />
          </Form.Group></Col>
          <Col><Form.Group className="mb-3">
            <Form.Label>Fecha límite <span className="text-muted">(opcional)</span></Form.Label>
            <Form.Control type="date" value={form.dueDate} onChange={set('dueDate')} />
          </Form.Group></Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>A quién se le debe <span className="text-muted">(opcional)</span></Form.Label>
          <Form.Control maxLength={120} value={form.payee} onChange={set('payee')} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Cuentas a donde se paga <span className="text-muted">(opcional)</span></Form.Label>
          {form.accounts.map((a, i) => (
            <Row key={i} className="g-2 mb-2">
              <Col xs={6}><Form.Control size="sm" value={a.number} onChange={setAccount(i, 'number')} placeholder="Número de cuenta" /></Col>
              <Col xs={5}><Form.Control size="sm" value={a.label} onChange={setAccount(i, 'label')} placeholder="Banco / titular" /></Col>
              <Col xs={1}><Button size="sm" variant="link" className="text-danger p-0" title="Quitar" onClick={() => setForm(f => ({ ...f, accounts: f.accounts.filter((_, j) => j !== i) }))}>✕</Button></Col>
            </Row>
          ))}
          <Button size="sm" variant="outline-secondary" onClick={() => setForm(f => ({ ...f, accounts: [...f.accounts, { number: '', label: '' }] }))}>+ Cuenta</Button>
          <Form.Text className="d-block text-muted">Las transferencias a estas cuentas que lleguen por WhatsApp se aplican a este compromiso. La primera se aprende sola del comprobante.</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Notas <span className="text-muted">(opcional)</span></Form.Label>
          <Form.Control as="textarea" rows={2} maxLength={1000} value={form.notes} onChange={set('notes')} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !form.name.trim() || !(Number(form.amount) > 0)}>{saving ? 'Guardando...' : 'Guardar'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function CommitmentDetail({ id, onClose, onEdit, onChanged }) {
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [printVouchers, setPrintVouchers] = useState(true);
  const load = useCallback(() => API.get(`/commitments/${id}`).then(r => setC(r.data)).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el compromiso.')), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, failure) => {
    setError('');
    try { await fn(); await load(); onChanged(); } catch (err) { setError(err.response?.data?.message || failure); }
  };
  const close = () => act(async () => { await API.post(`/commitments/${id}/close`, { reason }); setClosing(false); }, 'No se pudo cerrar el compromiso.');
  const reopen = () => act(() => API.post(`/commitments/${id}/reopen`), 'No se pudo reabrir el compromiso.');
  // The voucher of one payment, in a new tab (opened first: a pop-up opened after the download would be blocked).
  const openVoucher = async (expenseId) => {
    const tab = window.open('', '_blank');
    try {
      const { data } = await API.get(`/expenses/${expenseId}/voucher`, { responseType: 'blob' });
      if (tab) tab.location.href = URL.createObjectURL(data);
    } catch (err) {
      if (tab) tab.close();
      setError('No se pudo abrir el comprobante.');
    }
  };
  const print = () => window.open(`/expenses/compromisos/${id}/print${printVouchers ? '?comprobantes=1' : ''}`, '_blank');
  const remove = async () => {
    if (!window.confirm('¿Borrar este compromiso? No tiene pagos.')) return;
    setError('');
    try { await API.delete(`/commitments/${id}`); onChanged(); onClose(); } catch (err) { setError(err.response?.data?.message || 'No se pudo borrar el compromiso.'); }
  };
  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{c ? c.name : 'Compromiso'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        {!c ? <div className="text-muted">Cargando...</div> : (
          <>
            <Row className="mb-3 g-2">
              <Col xs={6} md={3}><div className="small text-muted">Total</div><strong>{money(c.amount)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Pagado</div><strong>{money(c.paid)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Pendiente</div><strong style={{ color: 'var(--mm-cyan)' }}>{money(c.balance)}</strong></Col>
              <Col xs={6} md={3}><div className="small text-muted">Estado</div><Badge bg={STATE[c.state]?.bg}>{STATE[c.state]?.label}</Badge></Col>
            </Row>
            <ProgressBar now={share(c)} variant={barVariant(c)} className="mb-2" style={{ height: 8 }} />
            <div className="small text-muted mb-3">
              {c.payee && <>Acreedor: {c.payee} · </>}Fecha límite: {c.dueDate ? formatDay(c.dueDate) : 'sin fecha'} · creado por {c.createdBy || '—'}
              <div className="mt-1">Cuentas: {c.accounts.length ? c.accounts.map(a => `${a.number}${a.label ? ` (${a.label})` : ''}`).join(' · ') : 'ninguna todavía (se aprende del primer comprobante)'}</div>
              {c.balance < -0.005 && <div className="mt-1 text-warning">Pagado de más: {money(-c.balance)}</div>}
              {c.notes && <div className="mt-1">{c.notes}</div>}
              {c.closedAt && <div className="mt-1 text-danger">Cerrado por {c.closedBy} el {formatDay(c.closedAt)}: {c.closeReason}</div>}
            </div>
            <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Table size="sm" className="mb-0 align-middle">
                <thead><tr><th>Fecha</th><th>Pago</th><th>Referencia</th><th>Comprobante</th><th className="text-end">Monto</th><th className="text-end">Pendiente</th></tr></thead>
                <tbody>
                  {c.entries.map(e => (
                    <tr key={e.expense}>
                      <td className="small">{formatDay(e.date)}</td>
                      <td className="small">{e.description || e.vendor || e.category}{e.toAccount && <div className="text-muted">a {e.toAccount}</div>}</td>
                      <td className="small">{e.reference || '—'}</td>
                      <td className="small">
                        {e.hasVoucher
                          ? <Button size="sm" variant="link" className="p-0" onClick={() => openVoucher(e.expense)}>📎 Ver</Button>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-end">{money(e.amount)}</td>
                      <td className="text-end">{money(e.balanceAfter)}</td>
                    </tr>
                  ))}
                  {c.entries.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">Todavía no hay pagos.</td></tr>}
                </tbody>
              </Table>
            </div>
            {closing && (
              <Form.Group className="mt-3">
                <Form.Label>¿Por qué se cierra? No se le aplicarán más pagos.</Form.Label>
                <Form.Control maxLength={300} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {c && !closing && (
          <>
            {c.payments === 0 && <Button variant="outline-danger" className="me-auto" onClick={remove}>Borrar</Button>}
            {c.payments > 0 && !c.closedAt && <Button variant="outline-danger" className="me-auto" onClick={() => setClosing(true)}>Cerrar compromiso</Button>}
            {c.closedAt && <Button variant="outline-secondary" className="me-auto" onClick={reopen}>Reabrir</Button>}
            <Form.Check type="switch" id="print-vouchers" className="small mb-0 me-1" label="con comprobantes" checked={printVouchers} onChange={e => setPrintVouchers(e.target.checked)} />
            <Button variant="outline-secondary" onClick={print}><i className="bi bi-printer me-1" />Imprimir reporte</Button>
            <Button variant="outline-secondary" onClick={() => onEdit(c)}>Editar</Button>
          </>
        )}
        {closing && (
          <>
            <Button variant="outline-secondary" onClick={() => setClosing(false)}>Volver</Button>
            <Button variant="danger" disabled={!reason.trim()} onClick={close}>Cerrar el compromiso</Button>
          </>
        )}
        <Button onClick={onClose}>Listo</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CommitmentsPanel;
